<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useProjectStore } from '@/stores/project';

import { useData } from '@/composables/useData';

import AppProjectSelector from '@/components/projects/ProjectSelector.vue';
import AppNavbarLogo from '@/components/general/NavbarLogo.vue';
// import AppSidemenuSkeleton from '@/components/general/SidemenuSkeleton.vue';

const emit = defineEmits(['closeSidebar']);

const projectStore = useProjectStore();

const route = useRoute();
const path = computed(() => route.path);
const projectId = computed(
  () => route.params.projectId || projectStore.currentProjectId
);

const { menuItems } = useData();

const projectSelectorRef = ref(null);

const projectListOpen = computed(
  () => projectSelectorRef.value?.showProjectList
);

watch(
  route,
  (newVal) => {
    if (newVal) {
      emit('closeSidebar');
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="sidebar-width flex h-full flex-col">
    <div class="flex-shrink-0 bg-white dark:bg-surface-800">
      <app-navbar-logo
        class="m-1 -ml-8 hidden md:block"
        :class="{
          'pointer-events-none md:pointer-events-auto': projectListOpen,
        }"
      />
      <div class="content-border-color border border-x-0 border-solid">
        <app-project-selector
          ref="projectSelectorRef"
          @closeSidebar="emit('closeSidebar')"
        />
      </div>
    </div>
    <div
      class="flex-1 overflow-y-auto bg-white dark:bg-surface-800"
      :class="{ 'blur-sm md:blur-none': projectListOpen }"
    >
      <Menu
        :key="projectId"
        :model="menuItems"
        class="h-full w-full rounded-none border-0"
        pt:root:class="dark:bg-surface-800 pt-5"
        pt:submenuLabel:class="!text-surface-500 dark:bg-surface-800 uppercase text-sm font-medium mt-4"
        :ptOptions="{ mergeProps: true }"
      >
        <template #submenuLabel="{ item }">
          <span
            class="font-medium text-surface-600 dark:bg-surface-800 dark:text-surface-0/80"
            >{{ item.label }}</span
          >
        </template>
        <template #item="{ item, props }">
          <router-link
            v-if="item.route"
            v-slot="{ href, navigate }"
            :to="{
              ...item.route,
              params: { projectId: projectId },
              force: true,
            }"
            custom
          >
            <a
              class="menu-item mb-0 font-medium !text-surface-700 dark:bg-surface-800 dark:!text-primary-50"
              :class="{
                'border-r-2 border-solid border-primary-500 bg-primary-50 dark:border-primary-300 dark:bg-highlight':
                  path.includes(item.route.path),
                'pointer-events-none md:pointer-events-auto': projectListOpen,
                'dark:hover:bg-surface-400/20 dark:hover:text-surface-300':
                  !path.includes(item.route.path),
              }"
              :href="href"
              v-permission="item.permission"
              v-ripple
              v-bind="props.action"
              @click="navigate"
            >
              <span :class="item.icon" />
              <span class="ml-2">{{ item.label }}</span>
            </a>
          </router-link>
        </template>
        <template #end>
          <br />
        </template>
      </Menu>
      <!-- <app-sidemenu-skeleton v-else :items="menuItems" /> -->
    </div>
  </div>
</template>
